import graphql__e62182cd19e0ccfaf254 from "./__generated__/HeaderWithSimpleMenu_platform.graphql.js";
import graphql__c7c73b5b0551eb0152f5 from "./__generated__/HeaderWithSimpleMenu_authenticationConfig.graphql.js";
import graphql__6a1398e6e8cc12362dcb from "./__generated__/HeaderWithSimpleMenu_viewer.graphql.js";
import graphql__740305633f92512969c2 from "./__generated__/HeaderWithSimpleMenu_navigationItems.graphql.js";import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";
import { CSSTransition } from "react-transition-group";
import classNames from "classnames";

import { useFormatMessage } from "../Intl/Context";
import SimpleMenuMainNavigation from "../MainNavigation/SimpleMenu/SimpleMenuMainNavigation";
import AdminLink from "./AdminLink";
import HeaderAuthenticatedUser from "./HeaderAuthenticatedUser";
import Button from "../Buttons/Button";
import { Desktop, NonDesktop, useResponsiveFlag } from "../Utils/ResponsiveQueries";
import PushableContainer from "../Utils/PushableContainer";
import Affix from "../Utils/Affix";
import { useComponentIsUnmounted } from "../Utils/useComponentIsUnmounted";
import { useRoutesContext } from "../Routes/RoutesContext";
import { intlMessages as headerIntlMessages } from "./Header";

import { setMobileHeaderHidden } from "../../reducers/styling";
import { useAffixHeaderOffsetTopUpdater } from "../Utils/useAffixHeaderOffsetTopUpdater";
import { useNonModalLocation } from "../Utils/routing";

// To make the affix header offset work correctly, we need to add it a 10px offset so the
// total calculated offset matches the actual needed height.
const AFFIX_PADDING_OFFSET = 10;

const HeaderWithSimpleMenu = props => {
  const platform = useFragment(HeaderWithSimpleMenu_platform, props.platform);
  const authenticationConfig = useFragment(
    HeaderWithSimpleMenu_authenticationConfig,
    props.authenticationConfig
  );
  const viewer = useFragment(HeaderWithSimpleMenu_viewer, props.viewer);
  const navigationItems = useFragment(
    HeaderWithSimpleMenu_navigationItems,
    props.navigationItems
  );

  const fullScreenMode = useSelector(({ styling }) => styling.fullScreenMode);
  const hideMobileHeader = useSelector(({ styling }) => styling.hideMobileHeader);
  const showHeader = useSelector(({ styling }) => styling.showHeader);
  const isBackdropOpen = useSelector(({ styling }) => styling.isBackdropOpen);

  const { pathname: currentPath } = useNonModalLocation();
  const { pathFor } = useRoutesContext();
  const homePath = pathFor("home");
  const isHome = currentPath === homePath;

  const nodeRef = useRef();
  const [navbarMenuContainer, setNavbarMenuContainer] = useState();

  const t = useFormatMessage();
  const homeLabel = t(headerIntlMessages.home);
  const menuAriaLabel = t(headerIntlMessages.mainMenu);
  const menuBurgerLabel = t(headerIntlMessages.pageList);

  const isUnmounted = useComponentIsUnmounted();
  const dispatch = useDispatch();
  const { isDesktop, isNonDesktop } = useResponsiveFlag();

  const { affixHeaderOffsetTop, updateAffixHeaderOffsetTop } =
    useAffixHeaderOffsetTopUpdater(() => {
      // If the navbarMenuContainer ref isn't defined we just return 0 to either wait for
      // the ref state callback setter to be called or that the menu is appended to the
      // DOM.
      //
      // Also, on the home page, we don't want any padding to be applied to the top of the
      // app container as there's already a static header at the top of the page, and the
      // fixed menu appears when the page is scrolled, and doesn't need any padding to be
      // applied to be displayed properly.
      if (!navbarMenuContainer || isHome) return 0;

      // Handle the case of the mobile "closed" menu, where we only display a burger menu
      // on the top left of the page. This is only used on map pages.
      if (isNonDesktop && fullScreenMode && hideMobileHeader) return AFFIX_PADDING_OFFSET;

      if (isDesktop || fullScreenMode) {
        return navbarMenuContainer.offsetTop + navbarMenuContainer.offsetHeight;
      } else {
        return navbarMenuContainer.offsetTop + AFFIX_PADDING_OFFSET;
      }
    }, [
      navbarMenuContainer,
      isHome,
      isNonDesktop,
      fullScreenMode,
      hideMobileHeader,
      isDesktop
    ]);

  const toggleMobileNavbar = useCallback(() => {
    dispatch(setMobileHeaderHidden(!hideMobileHeader));

    setTimeout(() => {
      if (isUnmounted()) return;

      updateAffixHeaderOffsetTop();
    }, 0);
  }, [dispatch, hideMobileHeader, isUnmounted, updateAffixHeaderOffsetTop]);

  useEffect(() => {
    if (navbarMenuContainer) updateAffixHeaderOffsetTop();
  }, [navbarMenuContainer, updateAffixHeaderOffsetTop]);

  const {
    id: platformId,
    logoBackgroundColor,
    logos,
    title: platformTitle,
    subtitle: platformSubtitle
  } = platform;
  const altText = [platformTitle, homeLabel].join(", ");

  const navbar = useMemo(() => {
    return (
      <div className="navbar-inner">
        <Desktop>
          <AdminLink />
        </Desktop>

        <div className="navbar-brand-container">
          <Link
            to={homePath}
            className="navbar-brand"
            key={platformId}
            style={{ backgroundColor: logoBackgroundColor }}
          >
            <img src={logos.legacyHeader} alt={altText} className="navbar-brand-logo" />
          </Link>

          <NonDesktop>
            <div className="navbar-brand-mobile-right-column">
              <div className="navbar-brand-platform-name">
                <span className="platform-title text-title-main-opening">
                  {platformTitle}
                </span>

                <span className="platform-subtitle text-subtitle-main-opening">
                  {platformSubtitle}
                </span>
              </div>

              <HeaderAuthenticatedUser
                viewer={viewer}
                authenticationConfig={authenticationConfig}
              />
            </div>
          </NonDesktop>
        </div>

        <div className="navbar-menu-placeholder" ref={setNavbarMenuContainer}>
          <Affix offsetTop={affixHeaderOffsetTop} className="navbar-menu-container">
            <PushableContainer
              className="navbar-menu-pushable-container w-100"
              preventPush={isDesktop}
            >
              <SimpleMenuMainNavigation
                navigationItems={navigationItems}
                viewer={viewer}
                authenticationConfig={authenticationConfig}
                platform={platform}
              />
            </PushableContainer>
          </Affix>
        </div>
      </div>
    );
  }, [
    affixHeaderOffsetTop,
    altText,
    authenticationConfig,
    homePath,
    isDesktop,
    logoBackgroundColor,
    logos.legacyHeader,
    navigationItems,
    platform,
    platformId,
    platformSubtitle,
    platformTitle,
    viewer
  ]);

  const mobileNavbar = useMemo(() => {
    if (!fullScreenMode) return navbar;

    const toggleButton = (
      <Button
        aria-label={menuBurgerLabel}
        type="button"
        onClick={toggleMobileNavbar}
        className="btn btn-sm position-fixed main-header-fullscreen-burger"
        iconClassName="icon-menu"
        role="button"
        aria-expanded={!hideMobileHeader}
        style={{ top: affixHeaderOffsetTop + 10 }}
      />
    );

    if (!hideMobileHeader) {
      return (
        <>
          {toggleButton}
          {navbar}
        </>
      );
    } else {
      return toggleButton;
    }
  }, [
    affixHeaderOffsetTop,
    menuBurgerLabel,
    fullScreenMode,
    hideMobileHeader,
    navbar,
    toggleMobileNavbar
  ]);

  const pushableContainerClassName = classNames([
    "main-header-navbar-fixed-wrapper transition-exit-off",
    { "box-shadow": fullScreenMode }
  ]);

  const containerClassName = classNames([
    "main-header-navbar-wrapper",
    {
      container: !fullScreenMode,
      "container-fluid": fullScreenMode
    }
  ]);

  return (
    <CSSTransition
      nodeRef={nodeRef}
      classNames="transition-slide-from-top"
      timeout={{ enter: 300, exit: 0 }}
      in={showHeader && !isBackdropOpen}
      unmountOnExit
    >
      <PushableContainer
        ref={nodeRef}
        className={pushableContainerClassName}
        data-top-navbar
        preventPush={!isDesktop && fullScreenMode && hideMobileHeader}
      >
        <header role="banner" className={containerClassName}>
          <nav
            className="navbar navbar-expand-lg navbar-fixed-top main-header-navbar padding-none"
            role="navigation"
            aria-label={menuAriaLabel}
          >
            <Desktop>{navbar}</Desktop>
            <NonDesktop>{mobileNavbar}</NonDesktop>
          </nav>
        </header>
      </PushableContainer>
    </CSSTransition>
  );
};

const HeaderWithSimpleMenu_platform = graphql__e62182cd19e0ccfaf254;

const HeaderWithSimpleMenu_authenticationConfig = graphql__c7c73b5b0551eb0152f5;

const HeaderWithSimpleMenu_viewer = graphql__6a1398e6e8cc12362dcb;

const HeaderWithSimpleMenu_navigationItems = graphql__740305633f92512969c2;

export default HeaderWithSimpleMenu;
